import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useCallback, useState, useRef } from "react";
import { createPortal } from "react-dom";
const PresentationModal = ({ isOpen, onClose, cards, currentIndex, onNavigate }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const containerRef = useRef(null);
    const handleKeyDown = useCallback((event) => {
        if (!isOpen)
            return;
        switch (event.key) {
            case "ArrowLeft":
                event.preventDefault();
                if (currentIndex > 0) {
                    onNavigate(currentIndex - 1);
                }
                break;
            case "ArrowRight":
                event.preventDefault();
                if (currentIndex < cards.length - 1) {
                    onNavigate(currentIndex + 1);
                }
                break;
            case "Escape":
                event.preventDefault();
                onClose();
                break;
        }
    }, [isOpen, currentIndex, cards.length, onNavigate, onClose]);
    const requestFullscreen = useCallback(async () => {
        if (!containerRef.current)
            return;
        try {
            if (containerRef.current.requestFullscreen) {
                await containerRef.current.requestFullscreen();
            }
            else if (containerRef.current.webkitRequestFullscreen) {
                await containerRef.current.webkitRequestFullscreen();
            }
            else if (containerRef.current.msRequestFullscreen) {
                await containerRef.current.msRequestFullscreen();
            }
            setIsFullscreen(true);
        }
        catch (error) {
            console.warn("Fullscreen not supported or failed:", error);
        }
    }, []);
    const exitFullscreen = useCallback(async () => {
        try {
            if (document.exitFullscreen) {
                await document.exitFullscreen();
            }
            else if (document.webkitExitFullscreen) {
                await document.webkitExitFullscreen();
            }
            else if (document.msExitFullscreen) {
                await document.msExitFullscreen();
            }
            setIsFullscreen(false);
        }
        catch (error) {
            console.warn("Exit fullscreen failed:", error);
        }
    }, []);
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement);
            setIsFullscreen(isCurrentlyFullscreen);
        };
        document.addEventListener("fullscreenchange", handleFullscreenChange);
        document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
        document.addEventListener("msfullscreenchange", handleFullscreenChange);
        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
            document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
            document.removeEventListener("msfullscreenchange", handleFullscreenChange);
        };
    }, []);
    useEffect(() => {
        if (isOpen) {
            document.addEventListener("keydown", handleKeyDown);
            requestFullscreen();
        }
        else {
            document.removeEventListener("keydown", handleKeyDown);
            if (isFullscreen) {
                exitFullscreen();
            }
        }
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, handleKeyDown, requestFullscreen, exitFullscreen, isFullscreen]);
    const handleClose = useCallback(() => {
        if (isFullscreen) {
            exitFullscreen();
        }
        onClose();
    }, [isFullscreen, exitFullscreen, onClose]);
    if (!isOpen) {
        return null;
    }
    const currentCard = cards[currentIndex];
    const modalContent = (_jsxs("div", { ref: containerRef, className: "d-flex flex-column align-items-center justify-content-center p-20", style: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "#000",
            zIndex: 10000,
            overflow: "hidden",
        }, children: [_jsxs("div", { className: "d-flex align-items-center", style: {
                    position: "absolute",
                    top: "20px",
                    right: "20px",
                    zIndex: 11000,
                    gap: "10px",
                }, children: [_jsxs("div", { className: "p-10", style: {
                            color: "#fff",
                            fontSize: "16px",
                            fontWeight: "500",
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            borderRadius: "var(--ps-border-radius)",
                        }, children: [currentIndex + 1, " of ", cards.length] }), _jsx("button", { onClick: isFullscreen ? exitFullscreen : requestFullscreen, className: "p-10", style: {
                            background: "rgba(255, 255, 255, 0.1)",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            color: "#fff",
                            borderRadius: "var(--ps-border-radius)",
                            cursor: "pointer",
                            fontSize: "14px",
                        }, children: isFullscreen ? "Exit Fullscreen" : "Fullscreen" }), _jsx("button", { onClick: handleClose, className: "p-10", style: {
                            background: "rgba(255, 255, 255, 0.1)",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            color: "#fff",
                            borderRadius: "var(--ps-border-radius)",
                            cursor: "pointer",
                            fontSize: "14px",
                        }, children: "\u2715 Close" })] }), currentIndex > 0 && (_jsx("button", { onClick: () => onNavigate(currentIndex - 1), style: {
                    position: "absolute",
                    left: "20px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "#fff",
                    padding: "12px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "18px",
                    zIndex: 11000,
                }, children: "\u2190 Previous" })), currentIndex < cards.length - 1 && (_jsx("button", { onClick: () => onNavigate(currentIndex + 1), style: {
                    position: "absolute",
                    right: "20px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "#fff",
                    padding: "12px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "18px",
                    zIndex: 11000,
                }, children: "Next \u2192" })), _jsx("div", { className: "d-flex align-items-center justify-content-center", style: {
                    width: "100%",
                    height: "100%",
                    padding: "60px 80px",
                    boxSizing: "border-box",
                }, children: _jsx("div", { className: "d-flex align-items-center justify-content-center", style: {
                        width: "100%",
                        maxWidth: "1400px",
                        height: "100%",
                        transform: "scale(1)",
                        transformOrigin: "center",
                        overflow: "visible",
                    }, children: _jsx("div", { className: "presentation-mode", style: {
                            width: "100%",
                            height: "auto",
                            minHeight: "600px",
                            maxHeight: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transform: `scale(${Math.min(1.5, window.innerWidth / 1000)})`,
                            transformOrigin: "center",
                        }, children: currentCard &&
                            React.cloneElement(currentCard, {
                                className: "presentation-mode",
                            }) }) }) }), _jsx("div", { style: {
                    position: "absolute",
                    bottom: "20px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: "14px",
                    textAlign: "center",
                }, children: "Use \u2190 \u2192 arrow keys to navigate \u2022 Press ESC to close" })] }));
    return createPortal(modalContent, document.body);
};
export default PresentationModal;
//# sourceMappingURL=PresentationModal.js.map